import {
  DSBadge,
  DSButton,
  DSCardWrapper,
  DSCircularButton,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import {
  faDownload,
  faSyncAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNoteCreation, useSyncNotes } from "@hooks";
import { useTeam } from "@hooks/use-team";
import { useLiveQuery } from "dexie-react-hooks";
import { saveAs } from "file-saver";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { db } from "../../db";
import { alert } from "../common/Alert";
import PageTitle from "../layout/PageTitle";
import NoteCreatedAt from "../notebook/note/NoteCreatedAt";

export default function UnsyncedNote() {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const { currentTeam } = useTeam();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Returns undefined while loading, null if not found, or the recording object
  const recording = useLiveQuery(async () => {
    const item = await db.unsyncedRecordings.get(Number(recordingId));
    return item ?? null;
  }, [recordingId]);

  const { addToSyncNotesQueue, syncNotesQueue, uploadIndex } =
    useSyncNotes();
  const createNote = useNoteCreation({ variant: "quick" });

  const isSyncing = uploadIndex === Number(recordingId);
  const isQueued = syncNotesQueue.some(
    (q) => q.id === Number(recordingId),
  );

  // Blob URL lifecycle
  const blobURL = useMemo(
    () =>
      recording?.blob ? URL.createObjectURL(recording.blob) : null,
    [recording],
  );
  useEffect(() => {
    return () => {
      if (blobURL) URL.revokeObjectURL(blobURL);
    };
  }, [blobURL]);

  const createNewNote = useCallback(
    (
      audioStorageLink,
      storageObjectName,
      jobType,
      _isSyncedRecording,
      _isNoteFromFileUpload,
      recordingAs,
      title,
      recordingConsentEventUuid,
    ) =>
      createNote({
        audioStorageLink,
        storageObjectName,
        jobType,
        title,
        recordingAs,
        teamUuid: currentTeam?.uuid,
        recordingConsentEventUuid,
      }),
    [createNote, currentTeam?.uuid],
  );

  const handleSync = () => {
    if (isSyncing || isQueued || !recording) return;
    mixpanel.track("Synced Recording From Unsynced Note Page", {
      recordingId: recording.id,
      jobType: recording.jobType,
    });
    addToSyncNotesQueue(recording, createNewNote, () => {
      navigate("/dashboard");
    });
  };

  const handleDelete = () => {
    db.unsyncedRecordings.delete(Number(recordingId));
    alert("success", "Recording deleted.");
    setShowDeleteModal(false);
    navigate("/dashboard");
  };

  const handleDownload = () => {
    if (!recording?.blob) return;
    saveAs(
      recording.blob,
      `Scribenote-Recording-${moment(recording.saved_date).format(
        "MM-DD-YYYY-h-mm-a",
      )}.wav`,
    );
  };

  // Loading state (useLiveQuery returns undefined before first resolution)
  if (recording === undefined) {
    return null;
  }

  // Recording not found
  if (recording === null) {
    return (
      <>
        <PageTitle title="Unsynced Recording" />
        <div className="flex flex-col items-center justify-center py-xl gap-md">
          <p className="text-secondary">
            This recording could not be found.
          </p>
          <DSButton
            variant="secondary"
            onClick={() => navigate("/dashboard")}
          >
            Back to Inbox
          </DSButton>
        </div>
      </>
    );
  }

  const statusLabel = isSyncing
    ? "Syncing"
    : isQueued
      ? "Queued"
      : "Unsynced";
  const statusVariant = isSyncing || isQueued ? "cyan" : "red";

  return (
    <>
      <DSModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete this recording?"
        dataTestId="delete-recording-modal"
        actions={[
          {
            label: "Cancel",
            variant: "cancel",
            onClick: () => setShowDeleteModal(false),
            dataTestId: "delete-recording-cancel",
          },
          {
            label: "Yes, delete",
            variant: "primary",
            onClick: handleDelete,
            dataTestId: "delete-recording-confirm",
          },
        ]}
      >
        <p>Are you sure you&apos;d like to delete this recording?</p>
      </DSModal>

      <PageTitle title="Unsynced Recording" />

      {/* Sticky header — mirrors ExistingNoteFields Divider pattern */}
      <div className="sticky top-16 z-20 w-full my-5 bg-primary/90 backdrop-filter backdrop-blur-xs">
        {/* Status row */}
        <div className="flex items-center gap-sm mb-md">
          <DSBadge
            variant={statusVariant}
            size="sm"
            dataTestId="unsynced-note-status"
          >
            {statusLabel}
          </DSBadge>
          <NoteCreatedAt createdAt={recording.saved_date} />
        </div>

        {/* Audio player + actions */}
        <div className="flex flex-row items-center gap-sm mb-md">
          {blobURL && (
            <audio
              controls
              className="w-full rounded-lg"
              data-testid="unsynced-note-audio"
            >
              <source src={blobURL} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
          )}
          {recording.blob && (
            <DSCircularButton
              icon={faDownload}
              size="sm"
              onClick={handleDownload}
              dataTestId="unsynced-note-download"
            />
          )}
          <span className={isSyncing ? "animate-spin" : ""}>
            <DSCircularButton
              icon={faSyncAlt}
              size="sm"
              onClick={handleSync}
              disabled={isSyncing || isQueued}
              dataTestId="unsynced-note-sync"
            />
          </span>
          <DSCircularButton
            icon={faTrash}
            size="sm"
            variant="danger"
            onClick={() => setShowDeleteModal(true)}
            dataTestId="unsynced-note-delete"
          />
        </div>
      </div>

      {/* Info card */}
      <DSCardWrapper variant="warning">
        <div className="flex items-center gap-sm">
          <div>
            <p className="text-sm">
              This recording has not been uploaded to our servers, so
              we’re not able to turn it into a note for you yet. This
              usually happens if your internet connection was not
              strong enough after recording to complete the upload
              automatically.
            </p>
            <p className="text-sm">
              Don’t worry – you can click the sync button (
              <FontAwesomeIcon
                icon={faSyncAlt}
                className="text-primary shrink-0"
              />
              ) to try sending it to our servers to be processed into
              a note. Make sure you’re connected to internet before
              doing this.
            </p>
          </div>
        </div>
      </DSCardWrapper>
    </>
  );
}
